import React, { useMemo } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Avatar,
  Box,
  Flex,
  Text,
} from "@chakra-ui/react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  FiHome,
  FiCalendar,
  FiLogOut,
  FiRepeat,
  FiDollarSign,
  FiUsers,
  FiGrid,
  FiUser,
  FiBell,
} from "react-icons/fi";
import { EMP_BASE_PATH } from "../config";
import { useEmployeeAuth } from "../auth/EmployeeAuthContext";
import { MobileTabBar } from "./mobileUi";


// Cùng điều kiện hiển thị nhóm "Quản lý" như Dashboard (không đổi logic).
const SUPPORT_DEPARTMENT_CODES = [
  "PHONG_HANH_CHINH_NHAN_SU",
  "PHONG_KE_TOAN",
  "HO_TRO_BAN_HANG_CRM",
];

// ===== Menu nhóm cho bản web (desktop), style giống sider của admin =====
const buildMenu = (showManagement) => [
  { name: "Trang chủ", path: EMP_BASE_PATH, icon: FiHome, end: true },
  {
    name: "Chuyên cần",
    path: `${EMP_BASE_PATH}/chuyen-can`,
    icon: FiCalendar,
    children: [
      { name: "Chấm công", path: `${EMP_BASE_PATH}/cham-cong` },
      { name: "Lịch chuyên cần", path: `${EMP_BASE_PATH}/chuyen-can` },
      { name: "Đăng ký chấm công ngoài", path: `${EMP_BASE_PATH}/cham-cong-ngoai` },
      { name: "Đăng ký làm bù", path: `${EMP_BASE_PATH}/lam-bu` },
      { name: "Xin nghỉ phép", path: `${EMP_BASE_PATH}/nghi-phep` },
    ],
  },
  {
    name: "Tài chính",
    path: `${EMP_BASE_PATH}/tai-chinh/du-an`,
    icon: FiDollarSign,
    children: [
      { name: "Dự án", path: `${EMP_BASE_PATH}/tai-chinh/du-an` },
      { name: "Đợt mở bán", path: `${EMP_BASE_PATH}/tai-chinh/dot-mo-ban` },
      { name: "Khách hàng", path: `${EMP_BASE_PATH}/tai-chinh/khach-hang` },
      { name: "Booking", path: `${EMP_BASE_PATH}/tai-chinh/booking` },
      { name: "Locking", path: `${EMP_BASE_PATH}/tai-chinh/locking` },
      { name: "TTKQ", path: `${EMP_BASE_PATH}/tai-chinh/ttkq` },
      { name: "Hợp đồng mua bán", path: `${EMP_BASE_PATH}/tai-chinh/hop-dong-mua-ban` },
      { name: "Đăng ký chuyển giao dịch", path: `${EMP_BASE_PATH}/tai-chinh/chuyen-giao-dich` },
      { name: "Cộng tác viên", path: `${EMP_BASE_PATH}/tai-chinh/cong-tac-vien` },
      { name: "Thu nhập cá nhân", path: `${EMP_BASE_PATH}/tai-chinh/thu-nhap` },
      { name: "Chứng chỉ môi giới", path: `${EMP_BASE_PATH}/tai-chinh/chung-chi-moi-gioi` },
    ],
  },
  ...(showManagement
    ? [
        {
          name: "Quản lý",
          path: `${EMP_BASE_PATH}/quan-ly/nhan-vien`,
          icon: FiUsers,
          children: [
            { name: "Quản lý nhân viên", path: `${EMP_BASE_PATH}/quan-ly/nhan-vien` },
            { name: "Quản lý chuyên cần", path: `${EMP_BASE_PATH}/quan-ly/chuyen-can` },
            { name: "Quản lý bán hàng", path: `${EMP_BASE_PATH}/quan-ly/ban-hang` },
            { name: "Báo cáo thống kê", path: `${EMP_BASE_PATH}/quan-ly/bao-cao` },
            { name: "Phê duyệt nghỉ phép", path: `${EMP_BASE_PATH}/quan-ly/phe-duyet-nghi-phep` },
            {
              name: "Phê duyệt chấm công ngoài",
              path: `${EMP_BASE_PATH}/quan-ly/phe-duyet-cham-cong-ngoai`,
            },
            { name: "Quản lý CCMG", path: `${EMP_BASE_PATH}/quan-ly/ccmg` },
            { name: "Quản lý thu nhập", path: `${EMP_BASE_PATH}/quan-ly/thu-nhap` },
          ],
        },
      ]
    : []),
  {
    name: "Tiện ích",
    path: `${EMP_BASE_PATH}/dao-tao`,
    icon: FiGrid,
    children: [
      { name: "Đào tạo", path: `${EMP_BASE_PATH}/dao-tao` },
      { name: "Đánh giá cá nhân", path: `${EMP_BASE_PATH}/danh-gia-ca-nhan` },
      { name: "Giới thiệu việc làm", path: `${EMP_BASE_PATH}/gioi-thieu-viec-lam` },
      { name: "Chữ ký", path: `${EMP_BASE_PATH}/chu-ky` },
    ],
  },
  {
    name: "Tài khoản",
    path: `${EMP_BASE_PATH}/tai-khoan`,
    icon: FiUser,
    children: [
      { name: "Trang tài khoản", path: `${EMP_BASE_PATH}/tai-khoan` },
      { name: "Thông tin cá nhân", path: `${EMP_BASE_PATH}/tai-khoan/thong-tin` },
    ],
  },
];

const BRAND_GRADIENT = "var(--color-button)";

const DesktopSider = ({ menu }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;

  // Mở sẵn nhóm chứa route hiện tại
  const defaultIndex = useMemo(() => {
    const index = menu.findIndex((item) =>
      item.children?.some((sub) => path === sub.path || path.startsWith(`${sub.path}/`)),
    );
    return index >= 0 ? [index] : [];
  }, [menu, path]);

  const isActive = (item) => (item.end ? path === item.path : path === item.path);

  return (
    <Flex direction="column" h="100%">
      <Flex align="center" gap="10px" px="16px" h="80px" flexShrink={0}>
        <Flex
          w="38px"
          h="38px"
          borderRadius="10px"
          bgImage={BRAND_GRADIENT}
          align="center"
          justify="center"
          color="white"
          fontWeight={800}
        >
          MSH
        </Flex>
        <Text color="white" fontWeight={800} fontSize="17px" letterSpacing="0.5px">
          MSH GROUP
        </Text>
      </Flex>

      <Box flex="1" overflowY="auto">
        <Accordion allowToggle defaultIndex={defaultIndex} w="100%">
          {menu.map((item) => (
            <AccordionItem
              key={item.name}
              border="none"
              bg="var(--color-sidermain)"
              _hover={{ bg: "var(--color-sidersecondary)" }}
              transition="background-color 0.3s ease"
            >
              <AccordionButton
                display="flex"
                alignItems="center"
                gap="12px"
                p="12px 20px"
                onClick={() => {
                  if (!item.children) navigate(item.path);
                }}
                _hover={{ bg: "transparent" }}
              >
                <Flex as="span" flex="1" textAlign="left" align="center" gap="12px">
                  <Box as={item.icon} fontSize="18px" color="var(--color-main)" />
                  <Text
                    fontSize="15px"
                    fontWeight={600}
                    color={
                      isActive(item)
                        ? "var(--color-option-employee-hover)"
                        : "var(--color-main)"
                    }
                  >
                    {item.name}
                  </Text>
                </Flex>
                {item.children && <AccordionIcon color="var(--color-main)" />}
              </AccordionButton>

              {item.children && (
                <AccordionPanel pb="8px" pt="0">
                  {item.children.map((sub) => (
                    <NavLink key={sub.path} to={sub.path} style={{ textDecoration: "none" }}>
                      <Box p="10px 0px 10px 44px" cursor="pointer">
                        <Text
                          fontSize="14px"
                          fontWeight={500}
                          className={path === sub.path ? "text-liner" : "text-unclick"}
                        >
                          {sub.name}
                        </Text>
                      </Box>
                    </NavLink>
                  ))}
                </AccordionPanel>
              )}
            </AccordionItem>
          ))}
        </Accordion>
      </Box>

      <Box borderTop="1px solid rgba(255,255,255,0.08)" p="12px" flexShrink={0}>
        <SiderFooter />
      </Box>
    </Flex>
  );
};

const SiderFooter = () => {
  const { user, logout } = useEmployeeAuth();
  const navigate = useNavigate();

  return (
    <>
      <Flex align="center" gap="10px" px="6px" mb="10px">
        <Avatar size="sm" name={user?.fullName} src={user?.avatar} />
        <Box overflow="hidden">
          <Text color="white" fontSize="13px" fontWeight={600} noOfLines={1}>
            {user?.fullName || "Nhân viên"}
          </Text>
          <Text color="#8C97B6" fontSize="11px" noOfLines={1}>
            {user?.code || user?.phone || ""}
          </Text>
        </Box>
      </Flex>
      {user?.isAdmin && (
        <Flex
          as="button"
          onClick={() => navigate("/")}
          align="center"
          gap="10px"
          w="100%"
          px="14px"
          py="10px"
          borderRadius="10px"
          color="#C7D0E0"
          _hover={{ bg: "rgba(255,255,255,0.10)", color: "white" }}
        >
          <Box as={FiRepeat} fontSize="17px" />
          <Text fontSize="14px" fontWeight={500}>
            Chuyển sang Quản trị
          </Text>
        </Flex>
      )}
      <Flex
        as="button"
        onClick={logout}
        align="center"
        gap="10px"
        w="100%"
        px="14px"
        py="10px"
        borderRadius="10px"
        color="#C7D0E0"
        _hover={{ bg: "rgba(255,255,255,0.10)", color: "white" }}
      >
        <Box as={FiLogOut} fontSize="17px" />
        <Text fontSize="14px" fontWeight={500}>
          Đăng xuất
        </Text>
      </Flex>
    </>
  );
};

// Thanh header trên cùng của bản web, khớp header admin (chuông + người dùng)
const DesktopHeader = () => {
  const { user } = useEmployeeAuth();

  return (
    <Flex
      h="80px"
      flexShrink={0}
      align="center"
      justify="flex-end"
      gap="24px"
      px="24px"
      bg="var(--color-main)"
    >
      <Flex
        w="44px"
        h="44px"
        borderRadius="full"
        bg="var(--fill-avatar)"
        align="center"
        justify="center"
        color="var(--color-info-employee)"
      >
        <Box as={FiBell} fontSize="20px" />
      </Flex>
      <Flex align="center" gap="12px">
        <Avatar size="md" name={user?.fullName} src={user?.avatar} />
        <Box>
          <Text fontSize="15px" fontWeight={600} color="var(--color-header-employee)">
            {user?.fullName || "Nhân viên"}
          </Text>
          <Text fontSize="13px" color="var(--text-color-Subtittle)">
            {user?.jobPosition?.name || user?.jobTitle?.name || user?.code || ""}
          </Text>
        </Box>
      </Flex>
    </Flex>
  );
};

const Shell = ({ children }) => {
  const { user } = useEmployeeAuth();

  const isManager = (user?.jobPosition?.code || "") !== "NHAN_VIEN";
  const isSupportDepartment = SUPPORT_DEPARTMENT_CODES.includes(
    user?.supportDepartment?.code || "",
  );
  const menu = useMemo(
    () => buildMenu(isManager || isSupportDepartment),
    [isManager, isSupportDepartment],
  );

  return (
    <Flex minH="100vh" bg="var(--color-backgroundmain)">
      {/* ===== Sidebar (desktop) ===== */}
      <Box
        display={{ base: "none", md: "block" }}
        w="250px"
        flexShrink={0}
        bg="var(--color-sidermain)"
        position="sticky"
        top="0"
        h="100vh"
      >
        <DesktopSider menu={menu} />
      </Box>

      {/* ===== Main ===== */}
      <Flex direction="column" flex="1" minW="0">
        <Box display={{ base: "none", md: "block" }}>
          <DesktopHeader />
        </Box>

        <Box
          flex="1"
          p={{ base: "0", md: "24px" }}
          w="100%"
          bg={{ base: "#FFFFFF", md: "transparent" }}
        >
          {children}
        </Box>
      </Flex>

      {/* ===== Thanh tab dưới (mobile) - khớp mobile-app-v2 ===== */}
      <MobileTabBar />
    </Flex>
  );
};

export default Shell;
